import Component from '@glimmer/component';
import Chip from 'tio-reimagined/components/chip';
import {
  APPLICATION_STATUS_MAP,
  PROGRAM_INSTANCE_STATUS_MAP,
  type ChipIntent,
} from 'tio-employee/utils/tas/applicationStateMap';

const PAYMENT_STATE_MAP: Record<string, { label: string; intent: ChipIntent }> = {
  'TAS.ApplicationPaymentState.NONE': { label: 'No Payment', intent: 'default' },
  'TAS.ApplicationPaymentState.UNPAID': { label: 'Unpaid', intent: 'default' },
  'TAS.ApplicationPaymentState.PAID_IN_PART': { label: 'Partially Paid', intent: 'success' },
  'TAS.ApplicationPaymentState.PAID_IN_FULL': { label: 'Paid', intent: 'success' },
  'TAS.ApplicationPaymentState.REFUND_REQUESTED': { label: 'In Repayment', intent: 'danger' },
};

interface StatePillSignature {
  Args: {
    /** Raw state string (application, program instance, or payment state) */
    state: string | undefined;
    /** Override the intent color */
    intent?: ChipIntent;
  };
  Element: HTMLElement;
}

/**
 * StatePill - Translates raw TAS state enums into friendly Chip labels
 *
 * Looks up the state in APPLICATION_STATUS_MAP, PROGRAM_INSTANCE_STATUS_MAP,
 * and PAYMENT_STATE_MAP. Falls back to the raw string if no match is found.
 *
 * @example
 * ```gts
 * <StatePill @state={{program.state}} />
 * <StatePill @state={{application.paymentState}} />
 * ```
 */
export default class StatePill extends Component<StatePillSignature> {
  get label() {
    const state = this.args.state;
    if (!state) return '';

    const appStatus = APPLICATION_STATUS_MAP[state];
    if (appStatus) return appStatus.label;

    const programStatus = PROGRAM_INSTANCE_STATUS_MAP[state];
    if (programStatus) return programStatus.label;

    const paymentStatus = PAYMENT_STATE_MAP[state];
    if (paymentStatus) return paymentStatus.label;

    return state;
  }

  get intent(): ChipIntent {
    if (this.args.intent) return this.args.intent;

    const state = this.args.state;
    if (!state) return 'default';

    const appStatus = APPLICATION_STATUS_MAP[state];
    if (appStatus) return appStatus.intent;

    const programStatus = PROGRAM_INSTANCE_STATUS_MAP[state];
    if (programStatus) return programStatus.intent;

    const paymentStatus = PAYMENT_STATE_MAP[state];
    if (paymentStatus) return paymentStatus.intent;

    return 'primary';
  }

  <template>
    {{#if this.label}}
      <Chip @intent={{this.intent}} ...attributes>{{this.label}}</Chip>
    {{/if}}
  </template>
}
